// src/features/projects/OrgsTab.tsx
import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { listProjectOrgs, createProjectOrg, type ProjectOrg } from './orgsApi';
import { listCompanyUsers } from './api';
import {
    listOrgMembers,
    addOrgMember,
    updateOrgMember,
    removeOrgMember,
    type OrgMember,
} from './orgMembersApi';

type Props = { projectId: string };

// 내부 회사 사용자(프로젝트 owner company 소속) 목록용 최소 타입
type CompanyUserLite = {
    id: string;
    name?: string | null;
    email?: string | null;
};

export default function OrgsTab({ projectId }: Props) {
    const qc = useQueryClient();

    const [newOrgName, setNewOrgName] = useState('');
    // ✅ 조직 선택은 “멤버 추가” 액션용으로만 사용
    const [selectedOrgId, setSelectedOrgId] = useState<string>('');
    const [addUserId, setAddUserId] = useState<string>('');
    const [addCanEvEntry, setAddCanEvEntry] = useState<boolean>(true);
    const [err, setErr] = useState<string | null>(null);

    // -----------------------------
    // Orgs
    // -----------------------------
    const orgsQ = useQuery({
        queryKey: ['projectOrgs', projectId],
        enabled: !!projectId,
        queryFn: () => listProjectOrgs(projectId),
    });

    const orgs: ProjectOrg[] = Array.isArray(orgsQ.data) ? orgsQ.data : [];

    const orgNameById = useMemo(() => new Map(orgs.map((o) => [o.id, o.name])), [orgs]);

    // -----------------------------
    // Company Users (internal-only candidates)
    // IMPORTANT: queryKey must NOT collide with other members queries
    // -----------------------------
    const companyUsersQ = useQuery({
        queryKey: ['companyUsers', projectId], // ✅ 캐시 키 분리 (중요)
        enabled: !!projectId,
        queryFn: () => listCompanyUsers(projectId),
    });

    const companyUsers: CompanyUserLite[] = Array.isArray(companyUsersQ.data) ? companyUsersQ.data : [];

    const userOptions = useMemo(() => {
        return companyUsers
            .map((m) => ({
                userId: m.id,
                label: `${m.name ?? '—'} (${m.email ?? 'no-email'})`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [companyUsers]);

    // -----------------------------
    // Org Members (ALWAYS ALL)
    // - 사용자 리스트는 항상 전체 조직 멤버를 합쳐서 표시
    // -----------------------------
    const orgMembersQ = useQuery({
        queryKey: ['orgMembersAll', projectId, orgs.map((o) => o.id).join(',')],
        enabled: !!projectId,
        queryFn: async () => {
            if (!projectId) return [] as OrgMember[];
            if (orgs.length === 0) return [] as OrgMember[];

            const chunks = await Promise.all(orgs.map((o) => listOrgMembers(projectId, o.id)));
            const flat = chunks.flat().filter(Boolean) as OrgMember[];

            // 안전장치: 중복 제거
            const byId = new Map<string, OrgMember>();
            for (const m of flat) byId.set(m.id, m);
            return Array.from(byId.values());
        },
    });

    const orgMembers: OrgMember[] = Array.isArray(orgMembersQ.data) ? orgMembersQ.data : [];

    // -----------------------------
    // Actions
    // -----------------------------
    async function onCreateOrg() {
        setErr(null);
        const name = newOrgName.trim();
        if (!name) return;

        try {
            await createProjectOrg(projectId, name);
            setNewOrgName('');
            await qc.invalidateQueries({ queryKey: ['projectOrgs', projectId] });
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? '조직 생성 실패');
        }
    }

    async function onAddMember() {
        setErr(null);
        if (!projectId) return;

        if (!selectedOrgId) {
            setErr('조직을 선택한 후 멤버를 추가해 주세요.');
            return;
        }
        if (!addUserId) return;

        try {
            await addOrgMember(projectId, selectedOrgId, addUserId, addCanEvEntry);
            setAddUserId('');

            // 전체 리스트는 항상 all query로 보여주므로 all 키를 invalidate
            await qc.invalidateQueries({ queryKey: ['orgMembersAll', projectId] });
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? '멤버 추가 실패(권한/중복/내부회사 확인)');
        }
    }

    async function onToggleEv(orgId: string, memberId: string, next: boolean) {
        setErr(null);
        if (!projectId) return;

        try {
            await updateOrgMember(projectId, orgId, memberId, next);
            await qc.invalidateQueries({ queryKey: ['orgMembersAll', projectId] });
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? 'EV 권한 변경 실패');
        }
    }

    async function onRemove(orgId: string, memberId: string) {
        setErr(null);
        if (!projectId) return;

        try {
            await removeOrgMember(projectId, orgId, memberId);
            await qc.invalidateQueries({ queryKey: ['orgMembersAll', projectId] });
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? '멤버 삭제 실패');
        }
    }

    // 테이블 표시 정렬(원하면 바꾸셔도 됨)
    const orgMembersSorted = useMemo(() => {
        const copy = [...orgMembers];
        copy.sort((a, b) => {
            const ao = orgNameById.get((a as any).orgId) ?? '';
            const bo = orgNameById.get((b as any).orgId) ?? '';
            if (ao !== bo) return ao.localeCompare(bo);

            const an = (a.user?.name ?? '').toString();
            const bn = (b.user?.name ?? '').toString();
            return an.localeCompare(bn);
        });
        return copy;
    }, [orgMembers, orgNameById]);

    // -----------------------------
    // Render
    // -----------------------------
    return (
        <div className="rounded-2xl border bg-white p-4 space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-lg font-semibold">조직 (Org) & EV 입력 권한</div>
            </div>

            {err && <div className="text-sm text-red-600">{err}</div>}

            {/* 조직 생성 */}
            <div className="flex gap-2 items-center">
                <input
                    className="border rounded-md px-2 py-1 text-sm w-72"
                    placeholder="새 조직명 (예: 설계, 조달, PM, QC)"
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                />
                <button className="px-3 py-1 rounded-md bg-sky-600 text-white text-sm" onClick={onCreateOrg}>
                    조직 추가
                </button>
                {orgsQ.isLoading && <span className="text-xs text-gray-500">조직 로딩중…</span>}
            </div>

            {/* 멤버 추가 (조직 선택은 여기서만 사용) */}
            <div className="rounded-xl border p-3 space-y-2">
                <div className="text-sm font-medium">멤버 추가</div>

                <div className="flex gap-2 items-center flex-wrap">
                    <div className="text-sm text-gray-600 w-20">조직</div>
                    <select
                        className="border rounded-md px-2 py-1 text-sm w-72"
                        value={selectedOrgId}
                        onChange={(e) => setSelectedOrgId(e.target.value)}
                    >
                        <option value="">(조직 선택)</option>
                        {orgs.map((o) => (
                            <option key={o.id} value={o.id}>
                                {o.name}
                            </option>
                        ))}
                    </select>

                    <div className="text-sm text-gray-600 w-20">사용자</div>
                    <select
                        className="border rounded-md px-2 py-1 text-sm w-72"
                        value={addUserId}
                        onChange={(e) => setAddUserId(e.target.value)}
                        disabled={!selectedOrgId}
                        title={!selectedOrgId ? '조직을 먼저 선택해 주세요.' : undefined}
                    >
                        <option value="">{selectedOrgId ? '(내부 회사 사용자 선택)' : '(조직 선택 필요)'}</option>
                        {userOptions.map((u) => (
                            <option key={u.userId} value={u.userId}>
                                {u.label}
                            </option>
                        ))}
                    </select>

                    <label className="text-sm flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={addCanEvEntry}
                            onChange={(e) => setAddCanEvEntry(e.target.checked)}
                            disabled={!selectedOrgId}
                        />
                        EV 입력 허용
                    </label>

                    <button
                        className="px-3 py-1 rounded-md bg-gray-900 text-white text-sm disabled:opacity-50"
                        onClick={onAddMember}
                        disabled={!selectedOrgId || !addUserId}
                    >
                        추가
                    </button>

                    {companyUsersQ.isLoading && <span className="text-xs text-gray-500">사용자 로딩중…</span>}
                </div>

                <div className="text-xs text-gray-500">
                    * 멤버 후보는 <b>프로젝트 내부 회사</b> 사용자만 표시됩니다. (서버에서도 내부회사만 허용)
                </div>
            </div>

            {/* 조직 멤버 리스트: 항상 전체 */}
            <div className="border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="text-left px-3 py-2 w-40">조직</th>
                            <th className="text-left px-3 py-2">사용자</th>
                            <th className="text-left px-3 py-2 w-32">EV 입력</th>
                            <th className="text-left px-3 py-2 w-20">삭제</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orgMembersQ.isLoading ? (
                            <tr>
                                <td className="px-3 py-3 text-gray-500" colSpan={4}>
                                    로딩중…
                                </td>
                            </tr>
                        ) : orgMembersSorted.length === 0 ? (
                            <tr>
                                <td className="px-3 py-3 text-gray-500" colSpan={4}>
                                    조직 멤버가 없습니다.
                                </td>
                            </tr>
                        ) : (
                            orgMembersSorted.map((m) => {
                                const orgId = (m as any).orgId as string | undefined;
                                const orgName = orgId ? orgNameById.get(orgId) ?? '—' : '—';

                                return (
                                    <tr key={m.id} className="border-t">
                                        <td className="px-3 py-2">{orgName}</td>
                                        <td className="px-3 py-2">
                                            <div className="font-medium">{m.user?.name ?? '—'}</div>
                                            <div className="text-xs text-gray-500">{m.user?.email ?? ''}</div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <label className="inline-flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    checked={!!m.canEvEntry}
                                                    onChange={(e) => {
                                                        if (!orgId) return;
                                                        onToggleEv(orgId, m.id, e.target.checked);
                                                    }}
                                                    disabled={!orgId}
                                                    title={!orgId ? 'orgId가 없어 변경할 수 없습니다.' : undefined}
                                                />
                                                <span>허용</span>
                                            </label>
                                        </td>
                                        <td className="px-3 py-2">
                                            <button
                                                className="text-red-600 underline disabled:opacity-50"
                                                onClick={() => {
                                                    if (!orgId) return;
                                                    onRemove(orgId, m.id);
                                                }}
                                                disabled={!orgId}
                                                title={!orgId ? 'orgId가 없어 삭제할 수 없습니다.' : undefined}
                                            >
                                                삭제
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}